import { TpWebGL2DRenderData } from './interface-renderData';
import { TpWebGl2DContext, TpWebGl2DProgramContext, TpWebGl2DScale, WebGL2DRenderTypes } from './interfaces';
import { webGl2DBitmap } from './renderBitmap';
import { webGl2DColoredPoints } from './renderColoredPoints';
import { webGl2DColoredTriangles } from './renderColoredTriangles';
import { webGl2DColoredSimpleLines } from './renderSimpleLines';
import {
  codeFS_Triangles,
  codeFS_PointsAntiAliased,
  codeVS_Triangles,
  codeVS_Points,
  codeVS_Lines,
  codeFS_Lines,
  codeVS_SimpleLines,
  codeFS_SimpleLines,
  codeVS_Bitmap,
  codeFS_Bitmap,
} from './shaders';

function renderFactory(type: WebGL2DRenderTypes) {
  // Returns a renderer for each type of layer
  if (type == WebGL2DRenderTypes.COLORED_POINTS) return webGl2DColoredPoints;
  if (type == WebGL2DRenderTypes.SIMPLELINES) return webGl2DColoredSimpleLines;
  if (type == WebGL2DRenderTypes.COLORED_TRIANGLES) return webGl2DColoredTriangles;
  if (type == WebGL2DRenderTypes.BITMAP) return webGl2DBitmap;
  throw 'Invalid layer type';
}

function createShader(gl: any, type: any, source: string) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
  if (success) return shader;
  else {
    alert(`ERROR WHILE CREATING SHADER; ${gl.getShaderInfoLog(shader)}`);
    gl.deleteShader(shader);
  }
}

function createProgram(gl: any, codeVertexShader: string, codeFragmentSahder: string) {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, codeVertexShader);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, codeFragmentSahder);

  const program = gl.createProgram();
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  const success = gl.getProgramParameter(program, gl.LINK_STATUS);
  if (success) return program;
  else {
    alert(gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
  }
}

function createProgramColoredTriangles(gl: any): TpWebGl2DProgramContext {
  const program = createProgram(gl, codeVS_Triangles, codeFS_Triangles);
  const locAttribPosition = gl.getAttribLocation(program, 'a_position');
  const locAttribColor = gl.getAttribLocation(program, 'a_color');
  const locZoomFactor = gl.getUniformLocation(program, 'u_zoom_factor');
  const locPanOffset = gl.getUniformLocation(program, 'u_pan_offset');

  return {
    program,
    locZoomFactor,
    locPanOffset,
    locAttribPosition,
    locAttribColor,
  };
}

function createProgramColoredPoints(gl: any): TpWebGl2DProgramContext {
  const program = createProgram(gl, codeVS_Points, codeFS_PointsAntiAliased);
  const locAttribPosition = gl.getAttribLocation(program, 'a_position');
  const locAttribColor = gl.getAttribLocation(program, 'a_color');

  const locPointSize = gl.getUniformLocation(program, 'u_pointsize');
  const locZoomFactor = gl.getUniformLocation(program, 'u_zoom_factor');
  const locPanOffset = gl.getUniformLocation(program, 'u_pan_offset');

  return {
    program,
    locZoomFactor,
    locPanOffset,
    locAttribPosition,
    locAttribColor,
    locPointSize,
  };
}

function createProgramColoredLines(gl: any): TpWebGl2DProgramContext {
  const program = createProgram(gl, codeVS_Lines, codeFS_Lines);

  const locAttribPosition = gl.getAttribLocation(program, 'a_position');
  const locAttribNormal = gl.getAttribLocation(program, 'a_normal');
  const locAttribColor = gl.getAttribLocation(program, 'a_color');

  const locWeight = gl.getUniformLocation(program, 'u_weight');
  const locZoomFactor = gl.getUniformLocation(program, 'u_zoom_factor');
  const locPanOffset = gl.getUniformLocation(program, 'u_pan_offset');

  return {
    program,
    locZoomFactor,
    locPanOffset,
    locAttribPosition,
    locAttribNormal,
    locAttribColor,
    locWeight,
  };
}

function createProgramSimpleLines(gl: any): TpWebGl2DProgramContext {
  const program = createProgram(gl, codeVS_SimpleLines, codeFS_SimpleLines);

  const locAttribPosition = gl.getAttribLocation(program, 'a_position');
  const locAttribColor = gl.getAttribLocation(program, 'a_color');

  const locZoomFactor = gl.getUniformLocation(program, 'u_zoom_factor');
  const locPanOffset = gl.getUniformLocation(program, 'u_pan_offset');
  const locFixedColor = gl.getUniformLocation(program, 'u_fixed_color');

  return {
    program,
    locZoomFactor,
    locPanOffset,
    locAttribPosition,
    locAttribColor,
    locFixedColor,
  };
}

function createProgramBitmap(gl: any): TpWebGl2DProgramContext {
  const program = createProgram(gl, codeVS_Bitmap, codeFS_Bitmap);
  const locAttribPosition = gl.getAttribLocation(program, 'a_position');
  const locAttribTexCoord = gl.getAttribLocation(program, 'a_texCoord');

  const locZoomFactor = gl.getUniformLocation(program, 'u_zoom_factor');
  const locPanOffset = gl.getUniformLocation(program, 'u_pan_offset');
  const locFixedOpacity = gl.getUniformLocation(program, 'u_fixed_opacity');

  return {
    program,
    locZoomFactor,
    locPanOffset,
    locAttribPosition,
    locAttribColor: null,
    locAttribTexCoord,
    locFixedOpacity,
  };
}

export function initWebGL2D(
  canvas: HTMLCanvasElement,
  renderData: TpWebGL2DRenderData,
  scaleX: TpWebGl2DScale,
  scaleY: TpWebGl2DScale,
  pixelRatio: number,
): TpWebGl2DContext {
  const gl = canvas!.getContext('webgl2', {
    depth: false,
    premultipliedAlpha: false,
    alpha: false, // do not use any alpha in the backbuffer
    powerPreference: 'high-performance', //'low-power',
    antialias: true,
    preserveDrawingBuffer: true, // must be set in order to be able to capture bitmap data for download
  })!;

  const programContextColoredPoints = createProgramColoredPoints(gl);
  const programContextSimpleLines = createProgramSimpleLines(gl);
  const programContextColoredTriangles = createProgramColoredTriangles(gl);
  const programContextBitmap = createProgramBitmap(gl);

  const ctx: TpWebGl2DContext = {
    canvas,
    gl,
    backColor: renderData.backColor,
    programContextColoredPoints,
    programContextSimpleLines,
    programContextColoredTriangles,
    programContextBitmap,
    renderLayers: [],
    scaleX,
    scaleY,
    pixelRatio,
    zoomFactor: 1,
  };

  const idsMap: { [id: string]: boolean } = {};
  for (const dataLayer of renderData.layers) {
    if (!dataLayer.layerId || idsMap[dataLayer.layerId])
      // check that each layer has a unique id
      throw 'Invalid webgl2d layer id';
    const renderer = renderFactory(dataLayer.renderType);
    ctx.renderLayers.push(renderer.create(ctx, dataLayer));
  }

  return ctx;
}

export function updateWebGL2DData(ctx: TpWebGl2DContext, renderData: TpWebGL2DRenderData) {
  for (let layerNr = 0; layerNr < ctx.renderLayers.length; layerNr++) {
    const renderLayer = ctx.renderLayers[layerNr];
    const layerData = renderData.layers[layerNr];
    if (renderLayer.layerId != layerData.layerId)
      // for now, we expect layers not to change dynanically. In the future we could improve this
      throw 'Unmatching webgl2d layer id';
    renderFactory(renderLayer.renderType).updateData(renderLayer, layerData);
  }
}

export function renderWebGL2D(ctx: TpWebGl2DContext) {
  const { gl } = ctx;

  gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
  gl.clearColor(ctx.backColor.r / 255, ctx.backColor.g / 255, ctx.backColor.b / 255, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);

  for (const renderLayer of ctx.renderLayers) {
    const renderer = renderFactory(renderLayer.renderType);
    renderer.performRender(ctx, renderLayer);
  }
}
